import React from 'react';
import { GitCompare, ArrowRight, Plus, Minus, TrendingUp, TrendingDown } from 'lucide-react';
import { fmtEur, computeQuote } from './QuoteForm';

const FIELDS = [
    { key: 'vehicle', label: 'Veicolo' },
    { key: 'vehicle_type', label: 'Tipologia' },
    { key: 'floors', label: 'Numero piani' },
    { key: 'length', label: 'Lunghezza (m)' },
    { key: 'vehicle_price', label: 'Prezzo veicolo', money: true },
    { key: 'vehicles_count', label: 'Numero veicoli' },
    { key: 'list_price', label: 'Totale listino', money: true },
    { key: 'max_discount_price', label: 'Sconto massimo', money: true },
];

const norm = (v) => (v === '' || v === undefined ? null : v);
const show = (v, money) => (v === null || v === undefined || v === '' ? '—' : money ? fmtEur(Number(v)) : String(v));

const Old = ({ children }) => <span className="line-through text-red-600 bg-red-50 px-1 rounded-sm font-mono">{children}</span>;
const New = ({ children }) => <span className="text-emerald-700 bg-emerald-50 px-1 rounded-sm font-mono font-semibold">{children}</span>;

export const QuoteDiff = ({ prev, current }) => {
    if (!prev?.quote) return null;
    const a = prev.quote;
    const b = current;

    const fieldChanges = FIELDS.filter(f => String(norm(a[f.key]) ?? '') !== String(norm(b[f.key]) ?? ''));

    const aOpts = a.optionals || [];
    const bOpts = (b.optionals || []).filter(o => o.description || o.price !== '');
    const key = (o) => (o.description || '').trim().toLowerCase();
    const aMap = new Map(aOpts.map(o => [key(o), o]));
    const bMap = new Map(bOpts.map(o => [key(o), o]));
    const added = bOpts.filter(o => !aMap.has(key(o)));
    const removed = aOpts.filter(o => !bMap.has(key(o)));
    const changed = bOpts
        .filter(o => aMap.has(key(o)) && Number(aMap.get(key(o)).price || 0) !== Number(o.price || 0))
        .map(o => ({ ...o, oldPrice: aMap.get(key(o)).price }));

    const tA = computeQuote({ ...a, optionals: aOpts });
    const tB = computeQuote(b);
    const delta = tB.total - tA.total;

    const nothing = fieldChanges.length === 0 && added.length === 0 && removed.length === 0 && changed.length === 0 && delta === 0;

    return (
        <div className="rounded-sm border p-4 space-y-3" data-testid="quote-diff">
            <h4 className="font-heading uppercase text-sm tracking-wider flex items-center gap-2">
                <GitCompare size={14} /> Confronto con {a.number}
            </h4>

            {nothing && <p className="text-sm text-muted-foreground" data-testid="quote-diff-empty">Nessuna differenza rispetto alla revisione precedente.</p>}

            {fieldChanges.length > 0 && (
                <ul className="space-y-1 text-sm" data-testid="quote-diff-fields">
                    {fieldChanges.map(f => (
                        <li key={f.key} className="flex flex-wrap items-center gap-2" data-testid={`quote-diff-field-${f.key}`}>
                            <span className="text-muted-foreground w-36">{f.label}</span>
                            <Old>{show(a[f.key], f.money)}</Old>
                            <ArrowRight size={12} className="text-muted-foreground" />
                            <New>{show(b[f.key], f.money)}</New>
                        </li>
                    ))}
                </ul>
            )}

            {(added.length > 0 || removed.length > 0 || changed.length > 0) && (
                <div className="space-y-1 text-sm" data-testid="quote-diff-optionals">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Optional</p>
                    {added.map((o, i) => (
                        <div key={`a${i}`} className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-sm px-2 py-1" data-testid="quote-diff-opt-added">
                            <Plus size={12} /> <span className="flex-1">{o.description}</span>
                            <span className="font-mono">{fmtEur(Number(o.price || 0))}</span>
                        </div>
                    ))}
                    {removed.map((o, i) => (
                        <div key={`r${i}`} className="flex items-center gap-2 text-red-600 bg-red-50 rounded-sm px-2 py-1" data-testid="quote-diff-opt-removed">
                            <Minus size={12} /> <span className="flex-1 line-through">{o.description}</span>
                            <span className="font-mono line-through">{fmtEur(Number(o.price || 0))}</span>
                        </div>
                    ))}
                    {changed.map((o, i) => (
                        <div key={`c${i}`} className="flex items-center gap-2 rounded-sm px-2 py-1 bg-amber-50 text-amber-800" data-testid="quote-diff-opt-changed">
                            <ArrowRight size={12} /> <span className="flex-1">{o.description}</span>
                            <Old>{fmtEur(Number(o.oldPrice || 0))}</Old>
                            <New>{fmtEur(Number(o.price || 0))}</New>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-between border-t pt-2 text-sm">
                <span className="font-heading uppercase tracking-wider">Totale offerta</span>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-muted-foreground">{fmtEur(tA.total)}</span>
                    <ArrowRight size={12} className="text-muted-foreground" />
                    <span className="font-mono font-semibold">{fmtEur(tB.total)}</span>
                    {delta !== 0 && (
                        <span
                            className={`inline-flex items-center gap-1 font-mono text-xs px-1.5 py-0.5 rounded-sm ${delta > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}
                            data-testid="quote-diff-delta"
                        >
                            {delta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {delta > 0 ? '+' : '−'}{fmtEur(Math.abs(delta))}
                            {tA.total > 0 && ` (${delta > 0 ? '+' : '−'}${Math.abs((delta / tA.total) * 100).toFixed(1)}%)`}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};
